import { Timestamp } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { AsyncStatus } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import throttle from 'lodash/throttle';
import { Component } from 'react';

import { AddedCaller } from '../components/addedCaller';

import { PhoneBar } from './phoneBar';

type PropsT = {
  active: boolean;
  csid: number;
  requesterId: string | null;
  recordingAsyncStatus: AsyncStatus;
  muteAsyncStatus: AsyncStatus;
  holdAsyncStatus: AsyncStatus;
  listenAsyncStatus: AsyncStatus;
  muted: boolean;
  onHold: boolean;
  recordingEnabled: boolean;
  isCurrentlyRecording: boolean;
  assignedAt: string | null;
  conversationPauseCallRecording: (payload: { csid: number }) => Promise<any>;
  conversationResumeCallRecording: (payload: { csid: number }) => Promise<any>;
  telephonySendDigits: (digit: string) => void;
  telephonySetHold: (payload: { setTo: boolean; csid: string }) => Promise<any>;
  telephonySetMute: (payload: { setTo: boolean; csid: string }) => Promise<any>;
  twilioConnectCall: (payload: {
    csid: string;
    muted: boolean;
    isOnlyListening?: boolean;
  }) => Promise<any>;
  twilioDisconnectCall: (payload: { csid: string }) => Promise<any>;
  addCallerRemoveCallerRequest: (payload: {
    userId: string;
    csid: string;
  }) => Promise<any>;
  addCallerSetOnHoldRequest: (payload: {
    userId: string;
    csid: string;
    hold: boolean;
  }) => Promise<any>;
  canListen: boolean;
  isListening: boolean;
  isAssigned: boolean;
  className?: string;
  authors: { [key: string]: User } | null;
  agentId?: string;
  extraParties: { [key: string]: User };
};

type StateT = {
  sounds: Array<any>;
  lastUpdate: number;
  peakIn: number;
  peakOut: number;
};

export default class PhoneForm extends Component<PropsT, StateT> {
  volumeHandler: any;

  constructor(props: PropsT) {
    super(props);
    this.state = {
      sounds: [],
      lastUpdate: 0,
      peakIn: 0,
      peakOut: 0,
    };
    this.handleVolumeUpdate = throttle(this.handleVolumeUpdate.bind(this), 200);
    this.renderCaller = this.renderCaller.bind(this);

    this.setupVolumeHandler();
  }

  componentDidUpdate() {
    this.setupVolumeHandler();
  }

  componentWillUnmount() {
    if (window.currentPhoneCall) {
      window.currentPhoneCall.removeListener('volume', this.handleVolumeUpdate);
    }
  }

  setupVolumeHandler(): void {
    if (!this.volumeHandler) {
      if (!window.currentPhoneCall) {
        setTimeout(() => {
          this.setupVolumeHandler();
        }, 2000);

        return;
      }

      this.volumeHandler = window.currentPhoneCall.on(
        'volume',
        this.handleVolumeUpdate,
      );
    }
  }

  handleVolumeUpdate(inputVolume: number, outputVolume: number) {
    this.setState(({ peakOut, peakIn, sounds }: StateT) => {
      const output = peakOut ? outputVolume / peakOut : 0;
      const input = peakIn ? inputVolume / peakIn : 0;

      return {
        peakOut: outputVolume,
        peakIn: inputVolume,
        sounds: [
          ...sounds.slice(-999), // limit the amount of sounds so it doesn't grow forever
          {
            input,
            output,
          },
        ],
      };
    });
  }

  toggleTelephonyHold = () => {
    const { telephonySetHold, onHold, csid, requesterId } = this.props;
    telephonySetHold({
      setTo: !onHold,
      csid,
      requesterId,
    } as any);
  };

  muteConversation = () => {
    this.props
      .telephonySetMute({ setTo: true, csid: this.props.csid } as any)
      .then((response: any) => {
        console.info('muteConversation response', response);
      })
      .catch((exception: any) => {
        console.warn('muteConversation exception', exception);
      });
  };

  unmuteConversation = () => {
    this.props
      .telephonySetMute({ setTo: false, csid: this.props.csid } as any)
      .then((response: any) => {
        console.info('unmuteConversation response', response);
      })
      .catch((exception: any) => {
        console.warn('unmuteConversation exception', exception);
      });
  };

  toggleTelephonyMute = () => {
    // this.props.telephonySetMute({setTo: !this.props.muted, csid: this.props.csid})
    if (this.props.muted === true) {
      this.unmuteConversation();
    } else {
      this.muteConversation();
    }
  };

  toggleListening = () => {
    if (this.props.canListen && this.props.isListening === false) {
      this.listenConversation();
    } else if (this.props.canListen && this.props.isListening === true) {
      this.leaveConversation();
    } else {
      console.warn('toggleListening, Can not find conversation');
    }
  };

  listenConversation = () => {
    this.props
      .twilioConnectCall({
        csid: this.props.csid,
        muted: true,
        isOnlyListening: true,
      } as any)
      .catch(() => {});
  };

  leaveConversation = () => {
    this.props
      .twilioDisconnectCall({ csid: this.props.csid } as any)
      .then(() => {
        this.volumeHandler = null;
      })
      .catch(() => {});
  };

  toggleRecording = () => {
    if (this.props.isCurrentlyRecording) {
      this.props.conversationPauseCallRecording({ csid: this.props.csid });
    } else {
      this.props.conversationResumeCallRecording({ csid: this.props.csid });
    }
  };

  toggleAddCallerHold = (userId: string, onHold: boolean) => {
    const { addCallerSetOnHoldRequest, csid } = this.props;
    addCallerSetOnHoldRequest({
      hold: !onHold,
      csid,
      userId,
    } as any);
  };

  removeCallerRequest = (userId: string) => {
    const { addCallerRemoveCallerRequest, csid } = this.props;
    addCallerRemoveCallerRequest({
      csid,
      userId,
    } as any);
  };

  renderCaller({ user, onHold, callState }: any) {
    return (
      <AddedCaller
        key={user.id}
        className="added-caller"
        isOnHold={onHold}
        caller={user}
        onHoldToggle={() => this.toggleAddCallerHold(user.id, onHold)}
        onEndCall={() => this.removeCallerRequest(user.id)}
        callState={callState}
      />
    );
  }

  render() {
    const {
      assignedAt,
      muted,
      onHold,
      recordingAsyncStatus,
      muteAsyncStatus,
      holdAsyncStatus,
      listenAsyncStatus,
      telephonySendDigits,
      recordingEnabled,
      isCurrentlyRecording,
      isAssigned,
      canListen,
      isListening,
      className,
      extraParties,
    } = this.props;

    const { sounds } = this.state;

    const rootClass = classnames('phone-form', className);

    return (
      <div className={rootClass}>
        <PhoneBar
          className="phone-form__bar"
          isAssigned={isAssigned}
          canListen={canListen}
          isListening={isListening}
          duration={<Timestamp counter time={assignedAt as any} />}
          canRecord={recordingEnabled}
          isRecording={isCurrentlyRecording}
          sounds={sounds}
          toggleRecording={this.toggleRecording}
          toggleListening={this.toggleListening}
          muted={muted}
          onHold={onHold}
          recordingAsyncStatus={recordingAsyncStatus}
          muteAsyncStatus={muteAsyncStatus}
          holdAsyncStatus={holdAsyncStatus}
          listenAsyncStatus={listenAsyncStatus}
          onHoldToggle={this.toggleTelephonyHold}
          onMuteToggle={this.toggleTelephonyMute}
          onDial={telephonySendDigits}
        />
        {Object.values(extraParties).map(this.renderCaller)}
      </div>
    );
  }
}
